import {
  Check as CheckIcon,
  Close as CloseIcon,
  CloseFullscreen as CollapseIcon,
  Error as ErrorIcon,
  OpenInFull as ExpandIcon,
  Star as FeatureIcon,
  Info as InfoIcon,
  NotificationsNone as NotificationsNoneIcon,
  MarkEmailRead as ReadIcon,
  CheckCircle as SuccessIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios from "axios";
import { Bell, BellRing } from "lucide-react";
import moment from "moment";
import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { hostname, parseError } from "../utils";
import { useNotifications } from "../context/NotificationContext";

interface NotificationData {
  [key: string]: any;
}

interface Notification {
  id: string;
  title: string;
  message: string;
  type: "info" | "warning" | "error" | "success" | "feature";
  priority: "low" | "normal" | "high";
  isRead: boolean;
  isDismissed: boolean;
  isSystemWide: boolean;
  requiresAcknowledgment: boolean;
  acknowledgedAt: string | null;
  notificationData: NotificationData | null;
  createdDate: string;
  updatedDate: string | null;
}

interface NotificationDropdownProps {
  companyId?: string;
  getToken: () => Promise<string>;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  companyId,
  getToken,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingActions, setLoadingActions] = useState<Set<string>>(new Set());
  const [isMarkingAllRead, setIsMarkingAllRead] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Use the notification context for unread count
  const { unreadCount, refreshUnreadCount } = useNotifications();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isOpen = Boolean(anchorEl);

  // Fetch notifications
  const fetchNotifications = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = await getToken();
      const params = new URLSearchParams();
      if (companyId) params.append("companyId", companyId);
      params.append("limit", "20");

      const url = `${hostname}/notifications/?${params.toString()}`;
      console.log("Fetching notifications from:", url);

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("Notifications response:", response.data);
      setNotifications(response.data.notifications);
    } catch (err: any) {
      console.error("Error fetching notifications:", err);
      console.error("Error response:", err.response?.data);
      console.error("Error status:", err.response?.status);
      setError(parseError(err));
    } finally {
      setLoading(false);
    }
  };


  // Mark notification as read
  const markAsRead = async (notificationId: string) => {
    if (loadingActions.has(notificationId)) return;

    setLoadingActions((prev) => new Set(prev).add(notificationId));

    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/notifications/${notificationId}/read`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Update local state
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, isRead: true } : n))
      );

      // Refresh unread count using context
      await refreshUnreadCount();
    } catch (err) {
      console.error("Failed to mark as read:", err);
    } finally {
      setLoadingActions((prev) => {
        const newSet = new Set(prev);
        newSet.delete(notificationId);
        return newSet;
      });
    }
  };

  // Acknowledge notification
  const acknowledgeNotification = async (notificationId: string) => {
    if (loadingActions.has(notificationId)) return;

    setLoadingActions((prev) => new Set(prev).add(notificationId));

    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/notifications/${notificationId}/acknowledge`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Update local state
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId
            ? { ...n, acknowledgedAt: moment().toISOString(), isRead: true }
            : n
        )
      );

      // Refresh unread count using context
      await refreshUnreadCount();
    } catch (err) {
      console.error("Failed to acknowledge:", err);
    } finally {
      setLoadingActions((prev) => {
        const newSet = new Set(prev);
        newSet.delete(notificationId);
        return newSet;
      });
    }
  };

  // Dismiss notification
  const dismissNotification = async (notificationId: string) => {
    if (loadingActions.has(notificationId)) return;

    setLoadingActions((prev) => new Set(prev).add(notificationId));

    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/notifications/${notificationId}/dismiss`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Remove from local state
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));

      // Refresh unread count using context
      await refreshUnreadCount();
    } catch (err) {
      console.error("Failed to dismiss:", err);
    } finally {
      setLoadingActions((prev) => {
        const newSet = new Set(prev);
        newSet.delete(notificationId);
        return newSet;
      });
    }
  };

  // Get notification icon
  const getNotificationIcon = (type: string, priority: string) => {
    const color =
      priority === "high"
        ? "error"
        : priority === "normal"
        ? "primary"
        : "disabled";

    switch (type) {
      case "warning":
        return <WarningIcon color="warning" fontSize="small" />;
      case "error":
        return <ErrorIcon color="error" fontSize="small" />;
      case "success":
        return <SuccessIcon color="success" fontSize="small" />;
      case "feature":
        return <FeatureIcon color="secondary" fontSize="small" />;
      default:
        return <InfoIcon color={color} fontSize="small" />;
    }
  };

  // Get notification priority color
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high":
        return "error";
      case "normal":
        return "primary";
      case "low":
        return "default";
      default:
        return "default";
    }
  };

  // Format date using moment
  const formatDate = (dateString: string) => {
    const date = moment(dateString);
    const now = moment();
    const diffMins = now.diff(date, "minutes");
    const diffHours = now.diff(date, "hours");
    const diffDays = now.diff(date, "days");

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.format("MMM D, YYYY");
  };


  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    if (!isOpen) {
      fetchNotifications();
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAllAsRead = async () => {
    if (isMarkingAllRead) return;

    setIsMarkingAllRead(true);

    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/notifications/mark-all-read`,
        { companyId },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Update local state to mark all as read
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, isRead: true }))
      );

      // Refresh unread count using context
      await refreshUnreadCount();

      console.log(`Marked ${response.data.markedCount} notifications as read`);
    } catch (err) {
      console.error("Failed to mark all as read:", err);
    } finally {
      setIsMarkingAllRead(false);
    }
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        aria-label="notifications"
        sx={{ ml: 1 }}
      >
        <Badge badgeContent={unreadCount} color="error">
          {unreadCount > 0 ? <BellRing /> : <Bell />}
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: isMobile ? "90vw" : isExpanded ? "min(900px, 90vw)" : 400,
            height: isMobile ? "70vh" : isExpanded ? "min(800px, 85vh)" : 500,
            maxWidth: isExpanded ? "min(900px, 90vw)" : 400,
            maxHeight: isExpanded ? "min(800px, 85vh)" : 500,
            display: "flex",
            flexDirection: "column",
            ml: isMobile ? 1 : 0,
            mr: isMobile ? 1 : 0,
            transition: "width 0.2s ease-in-out, height 0.2s ease-in-out, max-width 0.2s ease-in-out, max-height 0.2s ease-in-out",
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {/* Header */}
        <Box
          sx={{
            p: isMobile ? 1.5 : 2,
            borderBottom: 1,
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant={isMobile ? "subtitle1" : "h6"}>
              Notifications
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {unreadCount > 0 && (
                <Button
                  size="small"
                  onClick={handleMarkAllAsRead}
                  disabled={isMarkingAllRead}
                  startIcon={isMarkingAllRead ? <CircularProgress size={14} /> : null}
                  sx={{ fontSize: isMobile ? "0.75rem" : undefined }}
                >
                  {isMarkingAllRead ? "Marking..." : "Mark all read"}
                </Button>
              )}
              {!isMobile && (
                <Tooltip title={isExpanded ? "Collapse" : "Expand for easier reading"}>
                  <IconButton
                    size="small"
                    onClick={() => setIsExpanded(!isExpanded)}
                    sx={{ ml: 0.5 }}
                  >
                    {isExpanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
          {unreadCount > 0 && (
            <Typography variant="body2" color="text.secondary">
              {unreadCount} unread
            </Typography>
          )}
        </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
              <CircularProgress size={24} sx={{ mt: 1 }} />
            </Box>
          ) : error ? (
            <Box sx={{ p: 2 }}>
              <Alert severity="error">
                {error}
              </Alert>
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ p: 3, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1 }}>
              <NotificationsNoneIcon
                sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                No notifications
              </Typography>
            </Box>
          ) : (
            notifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <MenuItem
                  sx={{
                    flexDirection: "column",
                    alignItems: "flex-start",
                    whiteSpace: "normal",
                    minHeight: "auto",
                    py: isMobile ? 1 : 1.5,
                    px: isMobile ? 1.5 : 2,
                    opacity: notification.isDismissed ? 0.6 : 1,
                    backgroundColor: !notification.isRead
                      ? "action.hover"
                      : "transparent",
                  }}
                  onClick={() =>
                    !notification.isRead && markAsRead(notification.id)
                  }
                >
                  {/* Header */}
                  <Box
                    sx={{
                      display: "flex",
                      width: "100%",
                      alignItems: "flex-start",
                      mb: 1,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: "auto", mr: 1, mt: 0.5 }}>
                      {getNotificationIcon(
                        notification.type,
                        notification.priority
                      )}
                    </ListItemIcon>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: !notification.isRead ? 600 : 400,
                            flex: 1,
                            mr: 1,
                          }}
                        >
                          {notification.title}
                        </Typography>

                        {/* Priority and type chips */}
                        <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                          {notification.isSystemWide && (
                            <Chip
                              label="System"
                              size="small"
                              variant="outlined"
                              sx={{
                                fontSize: isMobile ? "0.65rem" : "0.7rem",
                                height: isMobile ? 18 : 20,
                              }}
                            />
                          )}
                          {notification.priority === "high" && (
                            <Chip
                              label="High"
                              size="small"
                              color="error"
                              variant="outlined"
                              sx={{
                                fontSize: isMobile ? "0.65rem" : "0.7rem",
                                height: isMobile ? 18 : 20,
                              }}
                            />
                          )}
                        </Box>
                      </Box>

                      {/* Message */}
                      <Box
                        sx={{
                          mt: 0.5,
                          "& p": {
                            m: 0,
                            lineHeight: 1.4,
                            color: "text.secondary",
                            fontSize: "0.875rem",
                          },
                          "& a": {
                            color: "primary.main",
                            textDecoration: "none",
                            "&:hover": {
                              textDecoration: "underline",
                            },
                          },
                          "& ul, & ol": {
                            m: 0,
                            pl: 2,
                            color: "text.secondary",
                            fontSize: "0.875rem",
                          },
                          "& code": {
                            backgroundColor: "action.hover",
                            px: 0.5,
                            borderRadius: 0.5,
                            fontSize: "0.8rem",
                          },
                          "& pre": {
                            backgroundColor: "action.hover",
                            p: 1,
                            borderRadius: 1,
                            overflow: "auto",
                            "& code": {
                              backgroundColor: "transparent",
                              p: 0,
                            },
                          },
                        }}
                      >
                        <ReactMarkdown>{notification.message}</ReactMarkdown>
                      </Box>

                      {/* Footer */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mt: 1,
                        }}
                      >
                        <Typography variant="caption" color="text.disabled">
                          {formatDate(notification.createdDate)}
                        </Typography>

                        {/* Actions */}
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          {notification.requiresAcknowledgment &&
                            !notification.acknowledgedAt && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  acknowledgeNotification(notification.id);
                                }}
                                disabled={loadingActions.has(notification.id)}
                                title="Acknowledge"
                              >
                                {loadingActions.has(notification.id) ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <CheckIcon fontSize="small" />
                                )}
                              </IconButton>
                            )}

                          {!notification.isRead && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                markAsRead(notification.id);
                              }}
                              disabled={loadingActions.has(notification.id)}
                              title="Mark as read"
                            >
                              {loadingActions.has(notification.id) ? (
                                <CircularProgress size={16} />
                              ) : (
                                <ReadIcon fontSize="small" />
                              )}
                            </IconButton>
                          )}

                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              dismissNotification(notification.id);
                            }}
                            disabled={loadingActions.has(notification.id)}
                            title="Dismiss"
                          >
                            {loadingActions.has(notification.id) ? (
                              <CircularProgress size={16} />
                            ) : (
                              <CloseIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </MenuItem>

                {index < notifications.length - 1 && <Divider />}
              </React.Fragment>
            ))
          )}
        </Box>
      </Menu>
    </>
  );
};

export default NotificationDropdown;
